import { FinalImpactScoreType } from '../types';

// https://docs.google.com/spreadsheets/d/1EZOkCum2fklwN-OhGGtKir7H8maSeGvMTYbwegm10rI#gid=1558950539
const costOfTotalGhgEmissions = 17.9 * Math.pow(10, 12);
const legacyWeightForGhgEmissions = 1.599;
/**
 * USD/year per point of the economic-cost factors (`impact_size_factor.economicCost`),
 * anchored on the global cost of GHG emissions.
 */
export const dollarCostPerLegacyCostUnit = costOfTotalGhgEmissions / legacyWeightForGhgEmissions;

// 04/2023: Decision by ET to hard code conversion rates constant over product scores,
// For more details and on options to change this, see https://docs.google.com/document/d/19jT-wyv9vUBdeh6ChbFRpCtvvX4QIt2n9OkWcmEEZUs/edit?pli=1,
const revenueOfAllCompaniesDollars = 301_416_143.7194522 * 1_000_000;
const centsInDollar = 100;
const legacyRelScoreToCentsPerDollarConversionMultiplier =
  (dollarCostPerLegacyCostUnit / revenueOfAllCompaniesDollars) * centsInDollar;

/**
 * See https://docs.google.com/document/d/1e2N9yXufmMOf_x77ukk3M8ABMb0yIBTJ2m_qfoNXM6A/edit#
 * for details for the conversion logic.
 */
export function legacyRelScoreToCentsPerDollar(relScore: number) {
  return relScore * legacyRelScoreToCentsPerDollarConversionMultiplier;
}
export function centsPerDollarToLegacyRelScore(centsPerDollar: number) {
  return centsPerDollar / legacyRelScoreToCentsPerDollarConversionMultiplier;
}
export function centsPerDollarToDollarsPerYear(centsPerDollar: number, revenue: number) {
  return (centsPerDollar * revenue) / centsInDollar;
}
export function legacyRelScoreToDollarsPerYear(relScore: number, revenue: number) {
  const centsPerDollar = legacyRelScoreToCentsPerDollar(relScore);
  return centsPerDollarToDollarsPerYear(centsPerDollar, revenue);
}
export function dollarsPerYearToLegacyRelScore(dollarsPerYear: number, revenue: number) {
  return (dollarsPerYear * centsInDollar) / revenue;
}

/**
 * Economy-wide value-add / revenue ratio (a weighted average from SDBS data).
 * Canonical home — `product-market-sizes/calculate.ts` imports this for
 * `globalTotalRevenue`, and the leena-2 harness uses it to value-add-correct
 * its CPDs (see {@link valueAddCorrectCpd}).
 */
export const AVERAGE_VALUE_ADDED = 0.250822;

/**
 * Rebase a leena-2 CPD from a revenue to a value-add basis. Leena CPDs are
 * cents-of-impact per dollar of *revenue*; summing cpd × revenue across
 * products over-counts, because revenue is counted at every supply-chain stage
 * (Σ revenue ≈ GDP / AVERAGE_VALUE_ADDED ≈ 4×GDP), whereas allocate-once
 * economic-cost factors are on a value-add basis (Σ ≈ GDP). Multiplying by the
 * economy-wide value-add/revenue ratio puts leena on the same basis. The factor
 * is a single constant, so it leaves NIRs and within-valence shapes unchanged.
 */
export function valueAddCorrectCpd(centsPerDollar: number): number {
  return centsPerDollar * AVERAGE_VALUE_ADDED;
}

/** {@link valueAddCorrectCpd} applied to a `${impactCode}_${valence}` → cpd map. */
export function valueAddCorrectCells(cells: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, cpd] of Object.entries(cells)) out[key] = valueAddCorrectCpd(cpd);
  return out;
}

export function legacyRelScoreToMonetaryUnit(
  relScore: number | undefined | null,
  unit: FinalImpactScoreType | undefined,
  revenue: number | undefined | null,
) {
  if (unit === undefined || relScore === null || relScore === undefined) return undefined;
  switch (unit) {
    case FinalImpactScoreType.RELATIVE:
      return relScore;
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return legacyRelScoreToCentsPerDollar(relScore);
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      if (!revenue) {
        return undefined;
      }
      return legacyRelScoreToDollarsPerYear(relScore, revenue);
    case FinalImpactScoreType.ABSOLUTE:
      // OH: Not implemented because not needed int he UI
      return undefined;
  }
}
